import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import './BillingReport.css';

const BillingReport = () => {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLeagues, setLoadingLeagues] = useState(true);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch leagues on mount
  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await api.get('/uniform-orders/leagues');
        setLeagues(response.data.leagues || []);
      } catch (err) {
        console.error('Error fetching leagues:', err);
        setError('Failed to load leagues');
      } finally {
        setLoadingLeagues(false);
      }
    };
    fetchLeagues();
  }, []);

  // Fetch report when league is selected
  useEffect(() => {
    if (!selectedLeague) {
      setReport(null);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/uniform-orders/billing/${encodeURIComponent(selectedLeague)}`);
        setReport(response.data);
      } catch (err) {
        console.error('Error fetching billing report:', err);
        setError('Failed to load billing report');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedLeague]);

  const filteredLeagues = leagues.filter(league =>
    league.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportOrdersPDF = () => {
    window.open(`/api/uniform-orders/billing/${encodeURIComponent(selectedLeague)}/orders-pdf`, '_blank');
  };

  const handleExportBreakdownPDF = () => {
    window.open(`/api/uniform-orders/billing/${encodeURIComponent(selectedLeague)}/breakdown-pdf`, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Build product rows from breakdown
  const getProductRows = () => {
    if (!report?.productBreakdown) return [];

    const breakdown = report.productBreakdown;
    const products = [
      { name: 'Jerseys', ...breakdown.jerseys },
      { name: 'Print Colors', ...breakdown.printColors },
      { name: 'Sponsor', ...breakdown.sponsor },
      { name: 'Numbers', ...breakdown.numbers },
      { name: 'Pants', ...breakdown.pants },
      { name: 'Caps', ...breakdown.caps },
      { name: 'Socks', ...breakdown.socks },
      { name: 'Belts', ...breakdown.belts }
    ];

    return products.filter(p => p.qty > 0).map(p => ({
      ...p,
      pricePerItem: p.qty > 0 ? p.total / p.qty : 0
    }));
  };

  return (
    <div className="billing-report">
      <div className="billing-report-header">
        <h2>Billing Report</h2>
      </div>

      {/* League Selector */}
      <div className="league-selector">
        <label>Select League:</label>
        <div className="league-dropdown" ref={dropdownRef}>
          <input
            type="text"
            placeholder={selectedLeague || "Search leagues..."}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            className="league-search"
          />
          {loadingLeagues ? (
            <div className="league-loading">Loading leagues...</div>
          ) : showDropdown && (
            <div className="league-options">
              {filteredLeagues.length === 0 ? (
                <div className="league-option no-results">No leagues found</div>
              ) : (
                filteredLeagues.map((league, index) => (
                  <div
                    key={index}
                    className={`league-option ${selectedLeague === league.customer_name ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedLeague(league.customer_name);
                      setSearchTerm('');
                      setShowDropdown(false);
                    }}
                  >
                    {league.customer_name}
                  </div>
                ))
              )}
            </div>
          )}
          {selectedLeague && (
            <button
              type="button"
              className="clear-selection"
              onClick={() => {
                setSelectedLeague('');
                setSearchTerm('');
                setReport(null);
              }}
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>

      {error && <div className="billing-error">{error}</div>}

      {loading && (
        <div className="billing-loading">
          <div className="spinner"></div>
          <p>Loading billing report...</p>
        </div>
      )}

      {report && !loading && (
        <div className="billing-content">
          {/* League Info */}
          <div className="league-info">
            <h3>{report.league.name}</h3>
            {(report.league.contact || report.league.phone) && (
              <p>
                Contact: {report.league.contact || 'N/A'} | Phone: {report.league.phone || 'N/A'}
              </p>
            )}
          </div>

          {/* Order History Section */}
          <div className="billing-section">
            <div className="section-header">
              <h4>Order History</h4>
              <button
                className="export-pdf-btn"
                onClick={handleExportOrdersPDF}
                disabled={report.orders.length === 0}
              >
                Export PDF
              </button>
            </div>
            {report.orders.length === 0 ? (
              <p className="no-data">No orders found for this league.</p>
            ) : (
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.orders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.orderNumber || 'N/A'}</td>
                      <td>{formatDate(order.date)}</td>
                      <td>
                        <span className={`status-badge status-${(order.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                          {order.status || 'N/A'}
                        </span>
                      </td>
                      <td>{order.items || 0}</td>
                      <td>{formatCurrency(order.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Product Breakdown Section */}
          <div className="billing-section">
            <div className="section-header">
              <h4>Product Breakdown</h4>
              <button
                className="export-pdf-btn"
                onClick={handleExportBreakdownPDF}
                disabled={getProductRows().length === 0}
              >
                Export PDF
              </button>
            </div>
            {getProductRows().length === 0 ? (
              <p className="no-data">No product data available.</p>
            ) : (
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Price/Item</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {getProductRows().map((product, index) => (
                    <tr key={index}>
                      <td>{product.name}</td>
                      <td>{product.qty}</td>
                      <td>{formatCurrency(product.pricePerItem)}</td>
                      <td>{formatCurrency(product.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Totals Section */}
          <div className="billing-totals">
            <div className="total-item">
              <span className="total-label">Total Items:</span>
              <span className="total-value">{report.totals.items}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Total Revenue:</span>
              <span className="total-value">{formatCurrency(report.totals.revenue)}</span>
            </div>
          </div>
        </div>
      )}

      {!selectedLeague && !loading && (
        <div className="billing-placeholder">
          <div className="placeholder-icon">📋</div>
          <p>Select a league to view their billing report</p>
        </div>
      )}
    </div>
  );
};

export default BillingReport;
